import React, { useEffect } from 'react';
import { SUBSCRIPTION_PLANS, initializeSubscriptionPlans } from '../../services/payment';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { PlanDetails } from '../../types/subscription';

interface PricingCardProps {
  plan: PlanDetails;
  onSubscribe: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({ plan, onSubscribe }) => {
  const { tier } = useSubscription();
  const isCurrentTier = tier === plan.id;

  return (
    <div className="bg-slate-800 rounded-lg p-6 flex flex-col">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-slate-100">{plan.name}</h3>
        <div className="mt-4">
          <span className="text-4xl font-bold text-slate-100">${plan.price}</span>
          <span className="text-slate-400 ml-2">{plan.interval === 'lifetime' ? ' ' + plan.interval : '/' + plan.interval}</span>
        </div>
      </div>

      <ul className="mb-8 flex-grow">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-center mb-3 text-slate-300">
            <svg
              className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            {feature}
          </li>
        ))}
      </ul>

      <button
        onClick={onSubscribe}
        disabled={isCurrentTier}
        className={`w-full py-3 px-4 rounded-md font-medium ${
          isCurrentTier
            ? 'bg-emerald-900 text-emerald-300 cursor-not-allowed'
            : 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
        }`}
      >
        {isCurrentTier ? 'Current Plan' : 'Subscribe'}
      </button>
    </div>
  );
};

interface SubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: (planId: string, paymentMethod: 'stripe' | 'bch') => void;
}

const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({
  isOpen,
  onClose,
  onSubscribe,
}) => {
  const { tier } = useSubscription();
  
  // Initialize subscription plans when component loads
  useEffect(() => {
    initializeSubscriptionPlans();
  }, []);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="fixed inset-0 bg-slate-900/80" 
        onClick={onClose}
      />
      
      <div className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl md:mx-4 flex flex-col">
        <div className="relative flex-1 bg-slate-900 md:rounded-lg shadow-xl w-full overflow-hidden border border-slate-700 flex flex-col">
          <div className="overflow-y-auto flex-1">
            <div className="p-6">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-100 mb-4">
                  Upgrade to Alpha - Ad-Free Experience
                </h2>
                <p className="text-slate-300">
                  Enjoy a premium, ad-free experience with faster responses and more features.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <PricingCard
                  plan={{
                    id: 'free',
                    name: 'Free (Ad-Supported)',
                    price: 0,
                    currency: 'USD',
                    interval: 'month',
                    features: [
                      'Ad-supported experience',
                      'Basic support',
                      'Standard response length'
                    ]
                  }}
                  onSubscribe={() => {}}
                />

                <PricingCard
                  plan={SUBSCRIPTION_PLANS.alpha}
                  onSubscribe={() => {
                    // Get the user's email from localStorage if available
                    const email = localStorage.getItem('userEmail');
                    console.log('Opening alpha upgrade page with email:', email);
                    
                    // If we have the email, include it as a URL parameter
                    if (email) {
                      window.open(`/alpha-upgrade?email=${encodeURIComponent(email)}`, '_blank');
                    } else {
                      window.open('/alpha-upgrade', '_blank');
                    }
                  }}
                />
              </div>

              <div className="mt-8 pt-6 border-t border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">
                  Payment Methods
                </h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      // Get the user's email from localStorage if available
                      const email = localStorage.getItem('userEmail');
                      console.log('Opening alpha upgrade page with email (Stripe button):', email);
                      
                      // If we have the email, include it as a URL parameter
                      if (email) {
                        window.open(`/alpha-upgrade?email=${encodeURIComponent(email)}`, '_blank');
                      } else {
                        window.open('/alpha-upgrade', '_blank');
                      }
                    }}
                    className="flex items-center px-4 py-2 bg-slate-800 rounded-md hover:bg-slate-700"
                  >
                    <img src="/stripe-logo.svg" alt="Stripe" className="h-6" />
                  </button>
                  <button
                    onClick={() => {
                      // Get the user's email from localStorage if available
                      const email = localStorage.getItem('userEmail');
                      console.log('Opening alpha upgrade page with email (BCH button):', email);
                      
                      // If we have the email, include it as a URL parameter
                      if (email) {
                        window.open(`/alpha-upgrade?email=${encodeURIComponent(email)}`, '_blank');
                      } else {
                        window.open('/alpha-upgrade', '_blank');
                      }
                    }}
                    className="flex items-center px-4 py-2 bg-slate-800 rounded-md hover:bg-slate-700"
                  >
                    <img src="/bch-logo.svg" alt="Bitcoin Cash" className="h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-400 hover:text-slate-200 p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDialog;
